<template>
  <div v-if="topic && currentStateConfig.visible" class="component is--full-detail-width-small">
    <div>
      <Heading :content="currentStateConfig.title" :level="2" class="heading">
        <span v-if="hasReplies"> ({{ repliesTotal }})</span>
      </Heading>

      <ol v-if="hasReplies" class="content">
        <li v-for="(reply, index) in replies" :key="index">
          <Reply v-bind="reply" />
        </li>
      </ol>

      <Alinea
        v-else
        variant="rte"
        :blocks="placeholderText.blocks"
        class="content info-block-text"
      />

      <div class="button-wrapper">
        <Button
          :to="topicUrl"
          :label="currentStateConfig.button_label"
          target="_blank"
          icon="eenvandaag-general:arrow-right"
          icon-position="right"
          type="cta"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IForumReply } from '~~/types/local_types';
import Reply from '~/components/page/replies/Reply.vue';

const config = useRuntimeConfig().public;
const { forumUrl } = config;

const props = defineProps({
  topic: {
    type: Number,
    default: null,
  },
});

const topicReactions = await useForumTopic.getLatestTopicReactions(props.topic as number);

const replies = ref<Array<IForumReply>>(topicReactions.replies);
const topicStatus = ref(topicReactions.topic_status);
const topicPostsApproved = ref(topicReactions.topic_posts_approved);

const hasReplies = computed(() => replies.value.length > 0);

const repliesTotal = computed(() => topicPostsApproved.value || 0);
const threadOpen = computed(() => topicStatus.value === 0);

const topicUrl = computed(() => {
  return `${forumUrl}viewtopic.php?t=${props.topic}`;
});

type ReplyState =
  | 'no-replies-open'
  | 'no-replies-closed'
  | 'has-replies-open'
  | 'has-replies-closed';

const state = computed<ReplyState>(() => {
  if (hasReplies.value) {
    return threadOpen.value ? 'has-replies-open' : 'has-replies-closed';
  }

  return threadOpen.value ? 'no-replies-open' : 'no-replies-closed';
});

const stateConfig: Record<
  ReplyState,
  { visible: boolean; title?: string; button_label?: string; description?: string }
> = {
  'no-replies-open': {
    visible: true,
    title: 'Nog geen reacties',
    button_label: 'Discussieer mee',
    description: 'Ga het gesprek aan met anderen en deel hoe jij over dit onderwerp denkt.',
  },
  'no-replies-closed': {
    visible: false,
  },
  'has-replies-open': {
    visible: true,
    title: 'Reacties',
    button_label: 'Discussieer mee',
  },
  'has-replies-closed': {
    visible: true,
    title: 'Reacties',
    button_label: 'Lees meer',
  },
};

const currentStateConfig = computed(() => stateConfig[state.value]);

const placeholderText = computed(() => {
  return {
    blocks: [
      {
        id: 1,
        type: 'paragraph',
        data: {
          text: currentStateConfig.value.description,
        },
      },
    ],
  };
});
</script>

<style lang="postcss" scoped>
.component {
  padding: var(--unit-16) var(--unit-18) var(--unit-24) var(--unit-18);

  background: hsl(var(--color-primary-50));

  @media (--lg-viewport) {
    padding: var(--unit-24) var(--unit-32) var(--unit-32) var(--unit-32);
  }
}

.heading {
  @mixin heading-lg;
  color: hsl(var(--color-primary-900)) !important;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--unit-20);

  margin: var(--unit-20) 0 var(--unit-16) 0;

  @media (--lg-viewport) {
    gap: var(--unit-24);
    margin: var(--unit-28) 0 var(--unit-24) 0;
  }
}

.info-block-text {
  position: relative;
  background-color: hsl(var(--color-neutral-white));
  border-radius: var(--unit-4);
  padding: var(--unit-12) var(--unit-18);

  @media (--lg-viewport) {
    padding: var(--unit-16) var(--unit-24);
  }
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
}
</style>
